/**
 * Word Toolbar Component
 * 
 * Provides standard word processor formatting tools for the Word editor using Tiptap.
 * Includes: text formatting, headings, lists, alignment, tables, images, and more.
 */

import * as React from 'react'
import { Editor } from '@tiptap/react'
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  ListBulletIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  LinkIcon,
  PhotoIcon,
  TableCellsIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline'
import {
  BoldIcon as BoldIconSolid,
  ItalicIcon as ItalicIconSolid,
  UnderlineIcon as UnderlineIconSolid,
} from '@heroicons/react/24/solid'
import clsx from 'clsx'

interface WordToolbarProps {
  editor: Editor
  theme?: 'light' | 'dark'
  onInsertLink?: () => void
  onInsertImage?: () => void
  onInsertTable?: () => void
}

interface ToolbarButtonProps {
  onClick: () => void
  isActive?: boolean
  icon: React.ReactNode
  label: string
  disabled?: boolean
  theme?: 'light' | 'dark'
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  onClick,
  isActive = false,
  icon,
  label,
  disabled = false,
  theme = 'light',
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'p-2 rounded-md transition-colors',
        theme === 'dark'
          ? 'text-gray-100 hover:bg-gray-700'
          : 'text-gray-700 hover:bg-gray-100',
        isActive &&
          (theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-gray-200'),
        disabled && 'opacity-50 cursor-not-allowed'
      )}
      title={label}
      aria-label={label}
    >
      {icon}
    </button>
  )
}

const WordToolbar: React.FC<WordToolbarProps> = ({
  editor,
  theme = 'light',
  onInsertLink,
  onInsertImage,
  onInsertTable,
}) => {
  if (!editor) {
    return null
  }

  // Insert table (delegates to parent if provided)
  const handleInsertTable = React.useCallback(() => {
    if (onInsertTable) {
      onInsertTable()
      return
    }
    try {
      editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
    } catch (error) {
      console.error('Failed to insert table:', error)
    }
  }, [editor, onInsertTable])

  // Insert image (delegates to parent if provided)
  const handleInsertImage = React.useCallback(() => {
    if (onInsertImage) {
      onInsertImage()
      return
    }
    const url = window.prompt('Enter image URL:')
    if (url) {
      try {
        editor.chain().focus().setImage({ src: url }).run()
      } catch (error) {
        console.error('Failed to insert image:', error)
      }
    }
  }, [editor, onInsertImage])

  // Insert link (delegates to parent if provided)
  const handleInsertLink = React.useCallback(() => {
    if (onInsertLink) {
      onInsertLink()
      return
    }
    const previousUrl = editor.getAttributes('link').href
    const url = window.prompt('URL', previousUrl)

    if (url === null) {
      return
    }

    try {
      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run()
      } else {
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
      }
    } catch (error) {
      console.error('Failed to insert link:', error)
    }
  }, [editor, onInsertLink])

  return (
    <div
      className={clsx(
        'flex items-center gap-1 p-2 border-b flex-wrap',
        theme === 'dark'
          ? 'bg-gray-900 border-gray-700'
          : 'bg-white border-gray-200'
      )}
    >
      {/* Text Formatting */}
      <div className="flex items-center gap-1 border-r border-gray-200 dark:border-gray-700 pr-2">
        <ToolbarButton
          theme={theme}
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          icon={editor.isActive('bold') ? <BoldIconSolid className="w-5 h-5" /> : <BoldIcon className="w-5 h-5" />}
          label="Bold (Ctrl+B)"
        />
        <ToolbarButton
          theme={theme}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          icon={editor.isActive('italic') ? <ItalicIconSolid className="w-5 h-5" /> : <ItalicIcon className="w-5 h-5" />}
          label="Italic (Ctrl+I)"
        />
        <ToolbarButton
          theme={theme}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          icon={editor.isActive('underline') ? <UnderlineIconSolid className="w-5 h-5" /> : <UnderlineIcon className="w-5 h-5" />}
          label="Underline (Ctrl+U)"
        />
      </div>

      {/* Headings */}
      <div className="flex items-center gap-1 border-r border-gray-200 dark:border-gray-700 pr-2">
        <select
          className={clsx(
            'px-2 py-1 text-sm border rounded-md',
            theme === 'dark'
              ? 'border-gray-600 bg-gray-800 text-gray-100'
              : 'border-gray-300 bg-white text-gray-900'
          )}
          onChange={(e) => {
            try {
              const value = e.target.value
              if (value === 'paragraph') {
                editor.chain().focus().setParagraph().run()
              } else if (value.startsWith('heading-')) {
                const level = parseInt(value.split('-')[1]) as 1 | 2 | 3 | 4 | 5 | 6
                editor.chain().focus().toggleHeading({ level }).run()
              }
            } catch (error) {
              console.error('Heading change error:', error)
            }
          }}
          value={
            editor.isActive('heading', { level: 1 }) ? 'heading-1' :
            editor.isActive('heading', { level: 2 }) ? 'heading-2' :
            editor.isActive('heading', { level: 3 }) ? 'heading-3' :
            editor.isActive('heading', { level: 4 }) ? 'heading-4' :
            editor.isActive('heading', { level: 5 }) ? 'heading-5' :
            editor.isActive('heading', { level: 6 }) ? 'heading-6' :
            'paragraph'
          }
        >
          <option value="paragraph">Normal</option>
          <option value="heading-1">Heading 1</option>
          <option value="heading-2">Heading 2</option>
          <option value="heading-3">Heading 3</option>
          <option value="heading-4">Heading 4</option>
          <option value="heading-5">Heading 5</option>
          <option value="heading-6">Heading 6</option>
        </select>
      </div>

      {/* Lists */}
      <div className="flex items-center gap-1 border-r border-gray-200 dark:border-gray-700 pr-2">
        <ToolbarButton
          theme={theme}
          onClick={() => {
            try {
              editor.chain().focus().toggleBulletList().run()
            } catch (e) {
              console.error('Toggle bullet list error:', e)
            }
          }}
          isActive={editor.isActive('bulletList')}
          icon={<ListBulletIcon className="w-5 h-5" />}
          label="Bullet List"
        />
        <ToolbarButton
          theme={theme}
          onClick={() => {
            try {
              editor.chain().focus().toggleOrderedList().run()
            } catch (e) {
              console.error('Toggle ordered list error:', e)
            }
          }}
          isActive={editor.isActive('orderedList')}
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
            </svg>
          }
          label="Numbered List"
        />
      </div>

      {/* Text Alignment */}
      <div className="flex items-center gap-1 border-r border-gray-200 dark:border-gray-700 pr-2">
        <ToolbarButton
          theme={theme}
          onClick={() => {
            try {
              editor.chain().focus().setTextAlign('left').run()
            } catch (e) {
              console.error('Text align error:', e)
            }
          }}
          isActive={editor.isActive({ textAlign: 'left' })}
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 6h18M3 14h18M3 18h18" />
            </svg>
          }
          label="Align Left"
        />
        <ToolbarButton
          theme={theme}
          onClick={() => {
            try {
              editor.chain().focus().setTextAlign('center').run()
            } catch (e) {
              console.error('Text align error:', e)
            }
          }}
          isActive={editor.isActive({ textAlign: 'center' })}
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 6h12M3 14h18M3 18h12" />
            </svg>
          }
          label="Align Center"
        />
        <ToolbarButton
          theme={theme}
          onClick={() => {
            try {
              editor.chain().focus().setTextAlign('right').run()
            } catch (e) {
              console.error('Text align error:', e)
            }
          }}
          isActive={editor.isActive({ textAlign: 'right' })}
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 6h18M3 14h18M3 18h18" />
            </svg>
          }
          label="Align Right"
        />
        <ToolbarButton
          onClick={() => {
            try {
              editor.chain().focus().setTextAlign('justify').run()
            } catch (e) {
              console.error('Text align error:', e)
            }
          }}
          isActive={editor.isActive({ textAlign: 'justify' })}
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 6h18M3 14h18M3 18h18" />
            </svg>
          }
          label="Justify"
        />
      </div>

      {/* Undo/Redo */}
      <div className="flex items-center gap-1 border-r border-gray-200 dark:border-gray-700 pr-2">
        <ToolbarButton
          theme={theme}
          onClick={() => {
            try {
              editor.chain().focus().undo().run()
            } catch (e) {
              console.error('Undo error:', e)
            }
          }}
          disabled={!editor.can().undo()}
          icon={<ArrowUturnLeftIcon className="w-5 h-5" />}
          label="Undo (Ctrl+Z)"
        />
        <ToolbarButton
          theme={theme}
          onClick={() => {
            try {
              editor.chain().focus().redo().run()
            } catch (e) {
              console.error('Redo error:', e)
            }
          }}
          disabled={!editor.can().redo()}
          icon={<ArrowUturnRightIcon className="w-5 h-5" />}
          label="Redo (Ctrl+Y)"
        />
      </div>

      {/* Insert */}
      <div className="flex items-center gap-1">
        <ToolbarButton
          theme={theme}
          onClick={handleInsertLink}
          icon={<LinkIcon className="w-5 h-5" />}
          label="Insert Link"
        />
        <ToolbarButton
          theme={theme}
          onClick={handleInsertImage}
          icon={<PhotoIcon className="w-5 h-5" />}
          label="Insert Image"
        />
        <ToolbarButton
          theme={theme}
          onClick={handleInsertTable}
          icon={<TableCellsIcon className="w-5 h-5" />}
          label="Insert Table"
        />
      </div>
    </div>
  )
}

export default WordToolbar
